import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useHistory} from 'src/rerouter';
import {useDispatch} from 'react-redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  SubTitleLarge,
  SubTitleExtraSmall,
  BodyLargeBold,
  BodyMedium,
  BodyMediumBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import type {CANDIDATE_PARSING} from 'src/types/crm';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {Truncate} from '@spaced-out/ui-design-system/lib/components/Truncate';
import {UnparsedResumePanel} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/unparsed-resume-panel';
import moment from 'moment';

import css from './parsing.css';


const formatTimestamp = (timestamp: number) => {
  return moment(timestamp).format('DD/MM/YYYY | hh:mm A');
};

export const InfoCard = ({
  parsingCandidate,
}: {
  parsingCandidate: CANDIDATE_PARSING;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [isUnParsedResumePanelOpen, setIsUnParsedResumePanelOpen] =
    React.useState(false);
  // @ts-expect-error - TS7030 - Not all code paths return a value.
  const getRightAction = React.useCallback(() => {
    if (parsingCandidate.duplicate && parsingCandidate.tried) {
      return (
        <div className={css.rightAction}>
          <Icon name="info-circle" size="small" color="warning" />
          <SubTitleExtraSmall color="warning">
            {t('Duplicate', 'Duplicate')}
          </SubTitleExtraSmall>
        </div>
      );
    } else if (
      !parsingCandidate.resumeParsed &&
      !parsingCandidate.parsed &&
      parsingCandidate.tried
    ) {
      return (
        <div className={css.rightAction}>
          {' '}
          <Button
            type="tertiary"
            onClick={() => setIsUnParsedResumePanelOpen(true)}
          >
            {t('Resolve', 'Resolve')}
          </Button>{' '}
        </div>
      );
    }
  }, [parsingCandidate]);

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  const getLeftIcon = React.useCallback(() => {
    if (parsingCandidate.parsed && parsingCandidate.tried) {
      return (
        <Icon name="check-circle" type="solid" size="small" color="success" />
      );
    } else if (!parsingCandidate.tried) {
      return <CircularLoader size="small" />;
    } else if (
      !parsingCandidate.resumeParsed &&
      !parsingCandidate.parsed &&
      parsingCandidate.tried
    ) {
      return (
        <Icon name="times-circle" type="solid" size="small" color="danger" />
      );
    }
  }, [parsingCandidate]);

  const getLastIndexValueFromFileName = React.useCallback(() => {
    const fileNameParts = parsingCandidate.filename.split('__');
    const fileName = fileNameParts[fileNameParts.length - 1];
    const lastIndexValue = fileName.split('.')[0].split('_')[1];
    return fileName;
  }, []);

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  const getTitle = React.useCallback(() => {
    if (!parsingCandidate) {
      return '';
    }

    const {jobId, candidateId, candidateName, filename, parsed, resumeParsed} =
      parsingCandidate;
    if (parsed) {
      return (
        <div className={css.row}>
          <Link
            as="bodyMedium"
            to={
              jobId
                ? `/crm/job/${jobId}/info/applicants/${candidateId}?selectedTab=overview`
                : `/crm/candidates/${candidateId}?selectedTab=overview`
            }
            color="clickable"
            underline={false}
            target="_blank"
          >
            <Truncate line={1} showFullTextOnHover={true}>
              {candidateName ? candidateName : filename.split('__')[2]}
            </Truncate>
          </Link>

          {candidateName && (
            <BodyMediumBold color="tertiary">
              <Truncate showFullTextOnHover={true} line={1}>
                ({getLastIndexValueFromFileName()})
              </Truncate>
            </BodyMediumBold>
          )}
        </div>
      );
    } else if (!resumeParsed && !parsed) {
      return (
        <BodyMediumBold>
          {candidateName ? candidateName : filename.split('__')[2]}
        </BodyMediumBold>
      );
    } else {
      <CircularLoader size="small" />;
    }
  }, []);

  return (
    <Card classNames={{wrapper: css.infoCardWrapper}}>
      <div className={css.rowSpaceBetween}>
        <div className={css.row}>
          {getLeftIcon()}
          {getTitle()}
        </div>
        {getRightAction()}
      </div>
      <div className={css.highlightItems}>
        <div className={css.highlightItem}>
          <Icon name="calendar" color="secondary" />
          <BodyMediumBold color="secondary">
            {formatTimestamp(parsingCandidate.addedOn)}
          </BodyMediumBold>
        </div>
        {parsingCandidate.jobTitle && (
          <div className={css.highlightItem}>
            <Icon name="briefcase" color="secondary" />
            <BodyMediumBold color="secondary">
              {parsingCandidate.jobTitle}
            </BodyMediumBold>
          </div>
        )}
      </div>
      <UnparsedResumePanel
        isOpen={isUnParsedResumePanelOpen}
        handleClose={() => setIsUnParsedResumePanelOpen(false)}
        unparsedResume={parsingCandidate.id}
      />
    </Card>
  );
};
